import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { CheckCircle } from 'lucide-react';
import PropertyFilters from './components/PropertyFilters';
import PropertyTable from './components/PropertyTable';
import AddPropertyModal from './components/AddPropertyModal';
import BulkActionsToolbar from './components/BulkActionsToolbar';
import PropertySidebar from './components/PropertySidebar';
import SurveyAssignmentModal from './components/SurveyAssignmentModal';
import { API_ENDPOINTS } from '../../utils/config';
import { useAuth } from '../../contexts/AuthContext';

const PropertyManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [editingProperty, setEditingProperty] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [clients, setClients] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isSurveyAssignmentModalOpen, setIsSurveyAssignmentModalOpen] = useState(false);
  const [selectedPropertyForSurvey, setSelectedPropertyForSurvey] = useState(null);
  const [propertySurveyStatus, setPropertySurveyStatus] = useState({}); // Track survey assignments
  const [coordinatorClients, setCoordinatorClients] = useState([]); // Track coordinator's assigned clients
  const [userRole, setUserRole] = useState(null); // Track user's role
  const [filters, setFilters] = useState({}); // Sync with PropertyFilters for category+filter coordination

  // Check if user is coordinator (must be defined before propertyBelongsToCoordinator)
  const isCoordinator = useMemo(() => {
    if (!user) {
      return false;
    }
    
    // Check roles array first (user.roles is an array)
    let roleName = '';
    if (user?.roles && Array.isArray(user.roles) && user.roles.length > 0) {
      // Handle both string array ["Coordinator"] and object array [{name: "Coordinator"}]
      const firstRole = user.roles[0];
      if (typeof firstRole === 'string') {
        roleName = firstRole;
      } else if (typeof firstRole === 'object' && firstRole !== null) {
        roleName = firstRole?.name || firstRole?.role_name || firstRole?.role || '';
      }
    }
    
    // Fallback to other role fields
    if (!roleName) {
      roleName = user?.role_detail?.name || 
                 user?.role?.name || 
                 user?.role_name || 
                 user?.role ||
                 user?.user_role?.role_detail?.name ||
                 user?.user_role?.role?.name ||
                 user?.user_role?.role_name ||
                 userRole || 
                 '';
    }
    
    const normalizedRole = String(roleName).toLowerCase().trim();
    const isCoord = normalizedRole.includes('coordinator') || normalizedRole === 'coordinator';
    
    return isCoord;
  }, [user, userRole]);

  // Helper function to check if property belongs to coordinator's clients
  const propertyBelongsToCoordinator = useMemo(() => {
    return (property, coordinatorClientsList) => {
      // If user has coordinator clients assigned, treat them as coordinator (even if isCoordinator is false)
      const hasCoordinatorClients = coordinatorClientsList && coordinatorClientsList.length > 0;
      
      // Non-coordinators (and users without coordinator clients) see all properties
      if (!isCoordinator && !hasCoordinatorClients) {
        return true;
      }
      
      // Coordinators with no clients assigned see no properties
      if ((isCoordinator || hasCoordinatorClients) && (!coordinatorClientsList || coordinatorClientsList.length === 0)) {
        return false;
      }

      const coordinatorClientIds = new Set(
        coordinatorClientsList
          .map(c => c?.id || c?.client?.id)
          .filter(id => id != null)
      );
      const coordinatorClientNames = new Set(
        coordinatorClientsList
          .map(c => {
            const name = c?.name || c?.client?.name || c?.client_details?.name || '';
            return name.toLowerCase().trim();
          })
          .filter(name => name.length > 0)
      );
      
      // Also extract base client names (e.g., "ICICI" from "ICICI - yelahanka")
      const coordinatorClientBaseNames = new Set(
        coordinatorClientsList
          .map(c => {
            const name = c?.name || c?.client?.name || c?.client_details?.name || '';
            // Extract base name before " - " (e.g., "ICICI" from "ICICI - yelahanka")
            const baseName = name.split(' - ')[0].split(' -')[0].trim();
            return baseName.toLowerCase();
          })
          .filter(name => name.length > 0)
      );
      
      // Check property.client field (string like "ICICI") - this is the primary check
      if (property?.client && typeof property.client === 'string') {
        const propertyClientName = property.client.toLowerCase().trim();
        
        // Check exact match
        if (coordinatorClientNames.has(propertyClientName)) {
          return true;
        }
        
        // Check base name match
        const propertyClientBaseName = propertyClientName.split(' - ')[0].split(' -')[0].trim();
        if (coordinatorClientBaseNames.has(propertyClientBaseName) || coordinatorClientNames.has(propertyClientBaseName)) {
          return true;
        }
        
        // Check if property client name matches any coordinator client name
        for (const clientName of coordinatorClientNames) {
          if (clientName === propertyClientName || 
              clientName.includes(propertyClientName) || 
              propertyClientName.includes(clientName)) {
            return true;
          }
        }
        
        // Check if property client base name matches any coordinator client base name
        for (const baseName of coordinatorClientBaseNames) {
          if (baseName === propertyClientBaseName || 
              baseName.includes(propertyClientBaseName) || 
              propertyClientBaseName.includes(baseName)) {
            return true;
          }
        }
      }

      // Check by client ID (only if client is not a string - if it's a string, we already checked it above)
      if (typeof property?.client !== 'string') {
        const propertyClientId = property?.client_id || 
                                 property?.client?.id || 
                                 property?.client ||
                                 property?.bank_id ||
                                 (typeof property?.client === 'number' ? property.client : null);
        
        if (propertyClientId != null) {
          const clientIdNum = typeof propertyClientId === 'number' ? propertyClientId : parseInt(propertyClientId);
          if (!isNaN(clientIdNum) && coordinatorClientIds.has(clientIdNum)) {
            return true;
          }
        }
      }
      
      // Check by client name (bank_name) - case-insensitive
      const propertyBankName = (property?.bank_name || property?.client_name || property?.name || '').toLowerCase().trim();
      if (propertyBankName) {
        // Extract base name from property (e.g., "ICICI" from "ICICI - yelahanka")
        const propertyBaseName = propertyBankName.split(' - ')[0].split(' -')[0].trim();
        
        // Check exact match
        if (coordinatorClientNames.has(propertyBankName)) {
          return true;
        }
        
        // Check if property base name matches any coordinator client name or base name
        if (coordinatorClientBaseNames.has(propertyBaseName) || coordinatorClientNames.has(propertyBaseName)) {
          return true;
        }
        
        // Check if property bank name starts with any coordinator client name
        for (const clientName of coordinatorClientNames) {
          if (propertyBankName.startsWith(clientName) || 
              propertyBankName.includes(` - ${clientName}`) ||
              propertyBankName.includes(` ${clientName}`)) {
            return true;
          }
        }
        
        // Check if property bank name starts with any coordinator client base name
        for (const baseName of coordinatorClientBaseNames) {
          if (propertyBankName.startsWith(baseName) || 
              propertyBankName.includes(` - ${baseName}`) ||
              propertyBankName.includes(` ${baseName}`)) {
            return true;
          }
        }
        
        // Reverse check: check if any coordinator client name contains the property base name
        for (const clientName of coordinatorClientNames) {
          if (clientName.includes(propertyBaseName) || clientName.startsWith(propertyBaseName)) {
            return true;
          }
        }
        
        // Reverse check: check if any coordinator client base name contains the property base name
        for (const baseName of coordinatorClientBaseNames) {
          if (baseName.includes(propertyBaseName) || baseName.startsWith(propertyBaseName)) {
            return true;
          }
        }
      }
      
      // Check if property's client object name matches
      const propertyClientName = (property?.client?.name || '').toLowerCase().trim();
      if (propertyClientName) {
        // Check exact match
        if (coordinatorClientNames.has(propertyClientName)) {
          return true;
        }
        // Check base name match
        const propertyClientBaseName = propertyClientName.split(' - ')[0].split(' -')[0].trim();
        if (coordinatorClientBaseNames.has(propertyClientBaseName) || coordinatorClientNames.has(propertyClientBaseName)) {
          return true;
        }
        // Check if property client name starts with any coordinator client name
        for (const clientName of coordinatorClientNames) {
          if (propertyClientName.startsWith(clientName) || propertyClientName.includes(` - ${clientName}`)) {
            return true;
          }
        }
        // Check if property client name starts with any coordinator client base name
        for (const baseName of coordinatorClientBaseNames) {
          if (propertyClientName.startsWith(baseName) || propertyClientName.includes(` - ${baseName}`)) {
            return true;
          }
        }
      }
      
      return false;
    };
  }, [isCoordinator]);

  // Fetch user's role
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user?.id) return;
      
      // First check user.roles array (most common structure)
      if (user?.roles && Array.isArray(user.roles) && user.roles.length > 0) {
        const firstRole = user.roles[0];
        let roleFromRolesArray = '';
        if (typeof firstRole === 'string') {
          roleFromRolesArray = firstRole;
        } else if (typeof firstRole === 'object' && firstRole !== null) {
          roleFromRolesArray = firstRole?.name || firstRole?.role_name || firstRole?.role || '';
        }
        if (roleFromRolesArray) {
          setUserRole(roleFromRolesArray);
          return;
        }
      }
      
      // Then check other user object fields
      const roleFromUser = user?.role_detail?.name || 
                          user?.role?.name || 
                          user?.role_name || 
                          user?.role ||
                          user?.user_role?.role_detail?.name ||
                          user?.user_role?.role?.name ||
                          user?.user_role?.role_name;
      
      if (roleFromUser) {
        setUserRole(roleFromUser);
        return;
      }

      // Try to fetch from USER_ROLES_LIST API
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Accept': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(API_ENDPOINTS.USER_ROLES_LIST, { 
          headers,
          validateStatus: (status) => status < 500
        });
        
        if (response.status === 200 || response.status === 201) {
          const userRolesData = Array.isArray(response.data)
            ? response.data
            : (response.data?.results || response.data?.data || response.data?.user_roles || []);

          const currentUserRole = userRolesData.find(ur => {
            const userId = ur?.user_detail?.id || ur?.user?.id;
            const username = ur?.user_detail?.username || ur?.user?.username;
            return userId === user.id || username === user.username;
          });

          if (currentUserRole) {
            const roleName = currentUserRole?.role_detail?.name || 
                            currentUserRole?.role?.name || 
                            currentUserRole?.role_name ||
                            currentUserRole?.role;
            setUserRole(roleName);
          }
        }
      } catch (error) {
        // Role fetch failed silently
      }
    };

    fetchUserRole();
  }, [user]);

  // Fetch coordinator's assigned clients
  useEffect(() => {
    const fetchCoordinatorClients = async () => {
      // If isCoordinator is false, try to check if user has coordinator assignments as a fallback
      if (!isCoordinator && user?.id) {
        try {
          const token = localStorage.getItem('token');
          const headers = {
            'Accept': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          };
          
          const assignmentsResponse = await axios.get(API_ENDPOINTS.COORDINATORS_ASSIGNMENTS_LIST, {
            headers,
            params: { coordinator_id: user.id, is_active: true },
            validateStatus: (status) => status < 500
          });
          
          if (assignmentsResponse.status === 200 || assignmentsResponse.status === 201) {
            const assignmentsData = Array.isArray(assignmentsResponse.data)
              ? assignmentsResponse.data
              : (assignmentsResponse.data?.results || assignmentsResponse.data?.data || []);
            
            if (assignmentsData.length > 0) {
              // User has coordinator assignments, continue to fetch their clients
            } else {
              setCoordinatorClients([]);
              return;
            }
          } else {
            setCoordinatorClients([]);
            return;
          }
        } catch (err) {
          setCoordinatorClients([]);
          return;
        }
      }
      
      if (!user?.id) {
        setCoordinatorClients([]);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Accept': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(API_ENDPOINTS.COORDINATORS_CLIENTS(user.id), { 
          headers,
          validateStatus: (status) => status < 500 // Don't throw for 403/404
        });
        
        if (response.status === 200 || response.status === 201) {
          // Handle different response structures
          let clientsData = [];
          if (response.data?.data && Array.isArray(response.data.data)) {
            // Response structure: { status: "ok", data: [{ client: {...} }] }
            clientsData = response.data.data.map(item => {
              // Extract client from nested structure
              return item?.client || item?.client_details || item;
            });
          } else if (Array.isArray(response.data)) {
            clientsData = response.data;
          } else if (response.data?.results) {
            clientsData = response.data.results;
          } else if (response.data?.clients) {
            clientsData = response.data.clients;
          }
          
          // Filter out null/undefined and ensure we have valid client objects
          clientsData = clientsData.filter(c => c && (c.id || c.client?.id));
          
          if (clientsData.length > 0) {
            setCoordinatorClients(clientsData);
            return;
          }
        }
      } catch (error) {
        // Continue to fallback method
      }
      
      // If endpoint fails or returns empty, try to get from assignments list
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Accept': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const assignmentsResponse = await axios.get(API_ENDPOINTS.COORDINATORS_ASSIGNMENTS_LIST, {
          headers,
          params: { coordinator_id: user.id, is_active: true },
          validateStatus: (status) => status < 500
        });
        
        if (assignmentsResponse.status === 200 || assignmentsResponse.status === 201) {
          const assignmentsData = Array.isArray(assignmentsResponse.data)
            ? assignmentsResponse.data
            : (assignmentsResponse.data?.results || assignmentsResponse.data?.data || []);
          
          // Extract unique clients from assignments
          const clientMap = new Map();
          assignmentsData.forEach(assignment => {
            const client = assignment?.client_details || 
                          assignment?.client || 
                          (assignment?.client_id ? { id: assignment.client_id } : null);
            
            if (client && client.id) {
              // Use client ID as key to avoid duplicates
              if (!clientMap.has(client.id)) {
                clientMap.set(client.id, client);
              }
            }
          });
          
          // Convert map to array
          const assignedClients = Array.from(clientMap.values());
          
          // If we have client IDs but not full client objects, fetch from clients list
          if (assignedClients.length > 0 && (!assignedClients[0].name && !assignedClients[0].client?.name && !assignedClients[0].client_details?.name)) {
            // Wait for clients to be loaded
            const clientsList = clients.length > 0 ? clients : await fetchClients();
            const clientIds = new Set(assignedClients.map(c => c.id || c.client?.id || c.client_details?.id).filter(Boolean));
            const fullClients = clientsList.filter(client => {
              const clientId = client?.id || client?.client?.id;
              return clientId && clientIds.has(clientId);
            });
            if (fullClients.length > 0) {
              setCoordinatorClients(fullClients);
              return;
            }
          }
          
          if (assignedClients.length > 0) {
            setCoordinatorClients(assignedClients);
            return;
          }
        }
      } catch (err) {
        // Silently handle - coordinator might not have assignments yet
        console.warn('Could not fetch coordinator clients:', err?.response?.status);
      }
      
      setCoordinatorClients([]);
    };

    fetchCoordinatorClients();
  }, [isCoordinator, user, clients]);

  // Fetch clients first, then properties (properties will be filtered after coordinator clients are loaded)
  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch properties after clients are loaded (for coordinators, wait for coordinatorClients too)
  useEffect(() => {
    if (!isCoordinator) {
      // Non-coordinators: fetch immediately
      fetchProperties();
    } else if (isCoordinator) {
      // Coordinators: fetch properties (will be filtered when coordinatorClients loads)
      // Don't wait for coordinatorClients - fetch now, filter later
      if (clients.length > 0) {
        fetchProperties();
      }
    }
  }, [clients, isCoordinator]);

  const fetchProperties = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.PROPERTY_LIST, { headers });
      
      let propertiesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.results || response.data?.data || response.data?.properties || []);
      
      // For coordinators (or users with coordinator clients): filter properties
      if (isCoordinator || coordinatorClients.length > 0) {
        if (coordinatorClients.length > 0) {
          // Filter properties for coordinators
          propertiesData = propertiesData.filter(property => 
            propertyBelongsToCoordinator(property, coordinatorClients)
          );
      setProperties(propertiesData);
      setFilteredProperties(propertiesData);
        } else {
          // Coordinator but clients not loaded yet - store all properties temporarily
          // Filtering will happen when coordinatorClients loads
          setProperties(propertiesData);
          setFilteredProperties([]); // Show empty until clients are loaded
        }
      } else {
        // Non-coordinator: show all properties
        setProperties(propertiesData);
        setFilteredProperties(propertiesData);
      }

      // Load assigned surveys and map to propertySurveyStatus
      await fetchAssignedSurveys(propertiesData);
    } catch (error) {
      setProperties([]);
      setFilteredProperties([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter properties when coordinator clients are loaded or when properties change
  useEffect(() => {
    // Check if user is coordinator OR has coordinator clients (fallback detection)
    const shouldFilter = isCoordinator || coordinatorClients.length > 0;
    
    if (shouldFilter) {
      if (coordinatorClients.length > 0 && properties.length > 0) {
        // Filter existing properties based on coordinator's clients
        const filtered = properties.filter(property => 
          propertyBelongsToCoordinator(property, coordinatorClients)
        );
        
        setFilteredProperties(filtered);
      } else if (coordinatorClients.length === 0 && properties.length > 0) {
        // If coordinator but no clients assigned yet, show empty list
        setFilteredProperties([]);
      }
    } else {
      // If not coordinator, show all properties
      setFilteredProperties(properties);
    }
  }, [coordinatorClients, isCoordinator, properties, propertyBelongsToCoordinator]);

  const fetchAssignedSurveys = async (propertiesData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const res = await axios.get(API_ENDPOINTS.SURVEYS_LIST, { headers });
      const surveysRaw = Array.isArray(res.data)
        ? res.data
        : (res.data?.results || res.data?.data || res.data?.surveys || []);

      // Helper function to normalize strings for comparison
      const normalizeString = (str) => {
        if (!str) return '';
        return String(str).trim().toLowerCase().replace(/\s+/g, ' ');
      };

      // Helper function to normalize phone numbers (remove spaces, dashes, etc.)
      const normalizePhone = (phone) => {
        if (!phone) return '';
        return String(phone).replace(/\D/g, ''); // Remove all non-digits
      };

      // Helper function to get client name from property
      const getPropertyClient = (property) => {
        return normalizeString(
          property?.client || 
          property?.client_name || 
          property?.bank_name ||
          property?.name
        );
      };

      // Helper function to get client name from survey
      const getSurveyClient = (survey) => {
        return normalizeString(
          survey?.client ||
          survey?.property_details?.client ||
          survey?.client_name ||
          survey?.bank_name
        );
      };

      const nextStatus = {};
      
      // For each survey, try to find matching property using multiple criteria
      (surveysRaw || []).forEach((s) => {
        let matchedProperty = null;
        let matchScore = 0;
        
        // Extract survey data
        const surveyClient = getSurveyClient(s);
        const surveyCustomerName = normalizeString(s?.customer_name || s?.property_details?.customer_name);
        const surveyContactPerson = normalizeString(s?.contact_person_name || s?.property_details?.contact_person_name);
        const surveyPhone = normalizePhone(s?.contact_person_phone_num || s?.property_details?.contact_person_phone_num || s?.phone_number);
        const surveyAddress = normalizeString(s?.property_address || s?.property_details?.property_address);
        const surveyPropertyId = s?.property_id || s?.property || s?.property?.id;

        // First, try exact property_id match (most reliable)
        if (surveyPropertyId != null) {
          matchedProperty = (propertiesData || []).find(p => String(p?.id) === String(surveyPropertyId));
          if (matchedProperty) {
            matchScore = 100;
          }
        }

        // If no property_id match, try matching by multiple fields
        if (!matchedProperty) {
          (propertiesData || []).forEach((property) => {
            let score = 0;
            const matches = [];

            // Match by client (weight: 20 points)
            const propertyClient = getPropertyClient(property);
            if (surveyClient && propertyClient && surveyClient === propertyClient) {
              score += 20;
              matches.push('client');
            }

            // Match by customer name (weight: 20 points)
            const propertyCustomerName = normalizeString(property?.customer_name);
            if (surveyCustomerName && propertyCustomerName && surveyCustomerName === propertyCustomerName) {
              score += 20;
              matches.push('customer_name');
            }

            // Match by contact person (weight: 20 points)
            const propertyContactPerson = normalizeString(property?.contact_person_name);
            if (surveyContactPerson && propertyContactPerson && surveyContactPerson === propertyContactPerson) {
              score += 20;
              matches.push('contact_person');
            }

            // Match by phone number (weight: 20 points)
            const propertyPhone = normalizePhone(property?.contact_person_phone_num || property?.phone_number);
            if (surveyPhone && propertyPhone && surveyPhone === propertyPhone && surveyPhone.length > 0) {
              score += 20;
              matches.push('phone');
            }

            // Match by address (weight: 20 points)
            const propertyAddress = normalizeString(property?.property_address);
            if (surveyAddress && propertyAddress && surveyAddress === propertyAddress) {
              score += 20;
              matches.push('address');
            }

            // Require at least 2 matching fields to consider it a match
            // This prevents false positives from single field matches
            if (score >= 40 && score > matchScore) {
              matchScore = score;
              matchedProperty = property;
            }
          });
        }

        // Only assign survey status if we found a valid property match AND survey has a survey number
        if (matchedProperty && matchedProperty.id && s?.survey_number) {
          nextStatus[String(matchedProperty.id)] = {
            surveyId: s?.id,
            surveyNumber: s?.survey_number,
            status: s?.status,
            surveyor: s?.surveyor,
            assignedAt: s?.created_at,
            propertyType: s?.property_type || '',
          };
        }
      });

      setPropertySurveyStatus(nextStatus);
    } catch (err) {
      // Non-fatal: if surveys fail to load, we just skip
      console.warn('Failed to fetch assigned surveys:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.CLIENTS_LIST, { headers });
      const clientsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.results || response.data?.data || response.data?.clients || []);
      
      setClients(clientsData);
      return clientsData; // Return for use in coordinator client fetching
    } catch (error) {
      setClients([]);
      return [];
    }
  };

  const applyCategoryFilter = useCallback((list, categoryIdOverride) => {
    const categoryId = categoryIdOverride ?? selectedCategory;
    if (!categoryId || categoryId === 'all' || ['high-value', 'favorites'].includes(categoryId)) return list;
    const propertyTypeIds = ['vacant-plot', 'land', 'residential', 'commercial', 'industrial', 'commercial-space', 'flat', 'villa', 'under-construction'];
    if (propertyTypeIds.includes(categoryId)) {
      const typeMap = {
        'vacant-plot': ['vacant plot'],
        land: ['land'],
        residential: ['residential'],
        commercial: ['commercial'],
        industrial: ['industrial'],
        'commercial-space': ['commercial space'],
        flat: ['flat'],
        villa: ['villa'],
        'under-construction': ['under construction']
      };
      const values = typeMap[categoryId] || [];
      return list.filter((p) => {
        const pt = (propertySurveyStatus[String(p?.id)]?.propertyType || '').toLowerCase().trim();
        return values.some((v) => pt === v || pt.includes(v));
      });
    }
    if (['pending', 'in-progress', 'completed', 'requires-update', 'assigned', 'unassigned'].includes(categoryId)) {
      return list.filter((p) => {
        const surveyInfo = propertySurveyStatus[String(p?.id)];
        const hasSurvey = surveyInfo && surveyInfo.surveyNumber;
        const status = (surveyInfo?.status || '').toString().toLowerCase().replace(/\s+/g, '_');
        if (categoryId === 'assigned') return !!hasSurvey;
        if (categoryId === 'unassigned') return !hasSurvey;
        if (categoryId === 'pending') return !hasSurvey || status === 'pending';
        if (categoryId === 'in-progress') return ['in_progress', 'in progress'].includes(status);
        if (categoryId === 'completed') return status === 'completed';
        if (categoryId === 'requires-update') return ['requires_update', 'requires-update', 'needs_update', 'needs update'].some((s) => status.includes(s));
        return false;
      });
    }
    if (categoryId === 'recent') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return list.filter((p) => new Date(p?.created_at || p?.createdAt || 0) >= sevenDaysAgo);
    }
    if (categoryId === 'expired') {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      return list.filter((p) => {
        const surveyInfo = propertySurveyStatus[p?.id];
        if (!surveyInfo?.status || surveyInfo.status !== 'completed') return false;
        const completedAt = surveyInfo?.completed_at || surveyInfo?.assignedAt;
        return completedAt && new Date(completedAt) < sixMonthsAgo;
      });
    }
    return list;
  }, [selectedCategory, propertySurveyStatus]);

  const applySearchAndPropertyFilters = useCallback((list, filtersToApply) => {
    let result = list ?? [];
    if (filtersToApply?.searchQuery) {
      const query = filtersToApply.searchQuery?.toLowerCase();
      result = result.filter(property => {
        const phones = Array.isArray(property?.contact_person_phone_num)
          ? property.contact_person_phone_num
          : [property?.contact_person_phone_num];
        const phoneMatch = phones.some(p => p?.toLowerCase()?.includes(query));
        return (
          property?.property_address?.toLowerCase()?.includes(query) ||
          property?.customer_name?.toLowerCase()?.includes(query) ||
          property?.bank_name?.toLowerCase()?.includes(query) ||
          property?.client?.toLowerCase()?.includes(query) ||
          property?.contact_person_name?.toLowerCase()?.includes(query) ||
          phoneMatch
        );
      });
    }
    if (filtersToApply?.propertyType) {
      const typeVal = (filtersToApply.propertyType || '').toString().toLowerCase().trim();
      result = result.filter(property => {
        const pt = (propertySurveyStatus[String(property?.id)]?.propertyType || '').toLowerCase().trim();
        return pt === typeVal || pt.includes(typeVal);
      });
    }
    return result;
  }, []);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters || {});
    // Start with coordinator-filtered properties (properties state is already filtered for coordinators)
    let filtered = isCoordinator && coordinatorClients.length > 0
      ? properties.filter(property => propertyBelongsToCoordinator(property, coordinatorClients))
      : [...properties];

    // Apply category filter from sidebar
    filtered = applyCategoryFilter(filtered);

    filtered = applySearchAndPropertyFilters(filtered, newFilters);

    setFilteredProperties(filtered);
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    let filtered = isCoordinator && coordinatorClients.length > 0
      ? properties.filter(property => propertyBelongsToCoordinator(property, coordinatorClients))
      : [...properties];

    // Apply category filter (pass new categoryId - state not updated yet)
    filtered = applyCategoryFilter(filtered, categoryId);
    filtered = applySearchAndPropertyFilters(filtered, filters);

    setFilteredProperties(filtered);
  };

  const handlePropertySelect = (propertyIds) => {
    setSelectedProperties(propertyIds);
  };

  const handleBulkAction = async (action, propertyIds = []) => {
    if (action === 'clear-selection') {
      setSelectedProperties([]);
      return;
    }

    if (action === 'delete' && propertyIds.length > 0) {
      if (!window.confirm(`Are you sure you want to permanently delete ${propertyIds.length} selected ${propertyIds.length === 1 ? 'property' : 'properties'}? This cannot be undone.`)) return;
      try {
        const token = localStorage.getItem('token');
        const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
        await Promise.all(propertyIds.map(id => axios.delete(API_ENDPOINTS.PROPERTY_DELETE(id), { headers })));
        setSuccessMessage(`${propertyIds.length} ${propertyIds.length === 1 ? 'property' : 'properties'} deleted successfully.`);
        setTimeout(() => setSuccessMessage(''), 4000);
        setSelectedProperties([]);
        await fetchProperties();
      } catch (error) {
        alert(error?.response?.data?.message || error?.response?.data?.detail || 'Failed to delete some properties. Please try again.');
      }
      return;
    }

    setTimeout(() => {
      setSelectedProperties([]);
    }, 1500);
  };

  const handleEditProperty = (property) => {
    setModalMode('edit');
    setEditingProperty(property);
    setIsAddModalOpen(true);
  };

  const handleDeleteProperty = (property) => {
    setPropertyToDelete(property);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!propertyToDelete) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        API_ENDPOINTS.PROPERTY_DELETE(propertyToDelete?.id),
        { 
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
        }
      );
      
      // Show success message from API response
      const message = response.data?.message || `Property deleted successfully`;
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(''), 5000); // 5 seconds for longer message
      
      // Refresh properties list
      await fetchProperties();
      
      // Close modal and clear selection
      const deletedId = propertyToDelete?.id;
      setIsDeleteModalOpen(false);
      setPropertyToDelete(null);
      setSelectedProperties(prev => prev.filter(id => id !== deletedId));
    } catch (error) {
      const errorMsg = error?.response?.data?.message || 
                       error?.response?.data?.detail || 
                       'Failed to delete property. Please try again.';
      alert(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setPropertyToDelete(null);
  };

  const handleBulkImport = () => {
    setIsBulkImportModalOpen(true);
    setSelectedFile(null);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xlsx')) {
        alert('Please select an XLSX file');
        event.target.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleImportSubmit = async () => {
    if (!selectedFile) {
      alert('Please select a file to import');
      return;
    }

    setIsImporting(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        API_ENDPOINTS.PROPERTY_BULK_IMPORT,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );

      // Close modal
      setIsBulkImportModalOpen(false);
      setSelectedFile(null);

      // Show success message with detailed counts
      const { imported_count, failed_count, total_rows, errors } = response.data;
      let message = `${imported_count} rows imported successfully!`;
      if (failed_count > 0) {
        message += ` (${failed_count} failed out of ${total_rows} total rows)`;
      }
      if (isCoordinator) {
        message += ` Properties are automatically assigned to your assigned clients.`;
      }
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(''), 5000);

      // Refresh the properties list (will be automatically filtered for coordinators)
      await fetchProperties();
      
      // Also refresh coordinator clients to ensure we have the latest assignments
      if (isCoordinator) {
        // Trigger coordinator clients refetch
        const fetchCoordinatorClients = async () => {
          if (!user?.id) return;
          try {
            const token = localStorage.getItem('token');
            const headers = {
              'Accept': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            };

            const response = await axios.get(API_ENDPOINTS.COORDINATORS_CLIENTS(user.id), { headers });
            
            let clientsData = [];
            if (response.data?.data && Array.isArray(response.data.data)) {
              clientsData = response.data.data.map(item => item?.client || item?.client_details || item);
            } else if (Array.isArray(response.data)) {
              clientsData = response.data;
            } else if (response.data?.results) {
              clientsData = response.data.results;
            } else if (response.data?.clients) {
              clientsData = response.data.clients;
            }
            
            clientsData = clientsData.filter(c => c && (c.id || c.client?.id));
            setCoordinatorClients(clientsData);
          } catch (error) {
            // Silently handle - will use fallback method
          }
        };
        await fetchCoordinatorClients();
      }

      // Show errors if any
      if (errors && errors.length > 0) {
        // Handle import errors silently
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || 
                       error?.response?.data?.detail || 
                       'Failed to import properties. Please try again.';
      alert(errorMsg);
    } finally {
      setIsImporting(false);
    }
  };

  const cancelBulkImport = () => {
    setIsBulkImportModalOpen(false);
    setSelectedFile(null);
  };

  const handleBulkExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(
        API_ENDPOINTS.PROPERTY_BULK_EXPORT,
        { 
          headers,
          responseType: 'blob'
        }
      );

      // Get filename from content-disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'properties_export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      // Show success message with row count
      const rowCount = properties.length;
      setSuccessMessage(`${rowCount} rows exported successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setSuccessMessage('');
      alert('Failed to export properties. Please try again.');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(
        API_ENDPOINTS.PROPERTY_DOWNLOAD_TEMPLATE,
        { 
          headers,
          responseType: 'blob'
        }
      );

      // Get filename from content-disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'property_template.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      // Show success message
      setSuccessMessage('Property template downloaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setSuccessMessage('');
      alert('Failed to download template. Please try again.');
    }
  };

  const handleAddProperty = async (responseData) => {
    // Show success message from API response
    const message = responseData?.message || (modalMode === 'edit' ? 'Property updated successfully' : 'Property created successfully');
    setSuccessMessage(message);
    
    // Clear message after 3 seconds
    setTimeout(() => setSuccessMessage(''), 3000);
    
    // Refresh properties list from API
    await fetchProperties();
  };

  const handleAssignSurvey = (property) => {
    setSelectedPropertyForSurvey(property);
    setIsSurveyAssignmentModalOpen(true);
  };

  const handleSurveyAssignmentSuccess = (message, surveyData) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 5000);
    
    // Update survey status for the property (only if survey has a survey number)
    if (selectedPropertyForSurvey && surveyData && surveyData.survey_number) {
      setPropertySurveyStatus(prev => ({
        ...prev,
        [selectedPropertyForSurvey.id]: {
          surveyId: surveyData.id,
          surveyNumber: surveyData.survey_number,
          status: surveyData.status,
          surveyor: surveyData.surveyor,
          assignedAt: surveyData.created_at
        }
      }));
    }
    
    // Close modal
    setIsSurveyAssignmentModalOpen(false);
    setSelectedPropertyForSurvey(null);
    
    // Refresh assigned surveys to get the latest data
    fetchAssignedSurveys(properties);
  };

  const handleSurveyAssignmentClose = () => {
    setIsSurveyAssignmentModalOpen(false);
    setSelectedPropertyForSurvey(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-screen">
          <div className="flex items-center space-x-2">
            <Icon name="Loader2" size={24} className="animate-spin" color="var(--color-primary)" />
            <span className="text-foreground">Loading properties...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16 flex">
        {/* Sidebar */}
        <div className="hidden lg:block">
          <PropertySidebar
            onCategorySelect={handleCategorySelect}
            selectedCategory={selectedCategory}
            properties={properties}
            propertySurveyStatus={propertySurveyStatus}
            className="h-screen sticky top-16"
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Property Management</h1>
              <p className="text-muted-foreground">
                Manage your property portfolio and track survey progress
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              <Button
                variant="outline"
                iconName="Map"
                iconPosition="left"
                onClick={() => navigate('/property-maps')}
              >
                View on Map
              </Button>
              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                onClick={handleBulkImport}
              >
                Import
              </Button>
              
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={handleBulkExport}
              >
                Export
              </Button>
              
              <Button
                variant="outline"
                iconName="FileDown"
                iconPosition="left"
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button>
              
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => {
                  setModalMode('add');
                  setEditingProperty(null);
                  setIsAddModalOpen(true);
                }}
              >
                Add Property
              </Button>
            </div>
          </div>

          {/* Mobile Category Selector */}
          <div className="lg:hidden mb-6">
            <PropertySidebar
              onCategorySelect={handleCategorySelect}
              selectedCategory={selectedCategory}
              properties={properties}
              propertySurveyStatus={propertySurveyStatus}
              className="w-full"
            />
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 rounded-lg border border-green-300 bg-green-50 text-green-700">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <PropertyFilters
            onFiltersChange={handleFiltersChange}
            className="mb-6"
          />

          {/* Bulk Actions */}
          {selectedProperties?.length > 0 && (
            <BulkActionsToolbar
              selectedCount={selectedProperties?.length}
              selectedPropertyIds={selectedProperties}
              onBulkAction={handleBulkAction}
              className="mb-6"
            />
          )}

          {/* Properties Table */}
          <div className="bg-card rounded-lg border border-border">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="Building" size={20} color="var(--color-primary)" />
                  <h2 className="text-lg font-semibold text-foreground">
                    Properties ({filteredProperties?.length})
                  </h2>
                </div>
                
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Filter" size={16} />
                  <span>
                    {selectedCategory === 'all' ? 'All Properties' : selectedCategory?.charAt(0)?.toUpperCase() + selectedCategory?.slice(1)?.replace('-', ' ')}
                  </span>
                </div>
              </div>
            </div>

            <PropertyTable
              properties={filteredProperties}
              onPropertySelect={handlePropertySelect}
              selectedProperties={selectedProperties}
              onPropertyEdit={handleEditProperty}
              onPropertyDelete={handleDeleteProperty}
              onAssignSurvey={handleAssignSurvey}
              clients={clients}
              propertySurveyStatus={propertySurveyStatus}
            />
          </div>

          {/* Empty State */}
          {filteredProperties?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="Building" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No properties found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your filters or add a new property to get started.
              </p>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setIsAddModalOpen(true)}
              >
                Add Your First Property
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Add/Edit Property Modal */}
      <AddPropertyModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddProperty}
        property={editingProperty}
        mode={modalMode}
      />

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg border border-border w-full max-w-md shadow-lg">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Delete Property</h3>
                  <p className="text-sm text-muted-foreground mt-1">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-foreground">
                  Are you sure you want to delete this property?
                </p>
                <div className="mt-2 p-3 bg-muted rounded-md">
                  <p className="text-sm font-medium text-foreground">{propertyToDelete?.property_address}</p>
                  <p className="text-xs text-muted-foreground mt-1">Customer: {propertyToDelete?.customer_name}</p>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  All associated data will be permanently removed from the system.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelDelete}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="default"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  loading={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Property'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {isBulkImportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg border border-border w-full max-w-md shadow-lg">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Bulk Import Properties</h3>
                  <p className="text-sm text-muted-foreground mt-1">Upload an XLSX file to import properties</p>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select XLSX File
                </label>
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-foreground
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-primary file:text-primary-foreground
                    hover:file:bg-primary/90
                    file:cursor-pointer cursor-pointer
                    border border-input rounded-md"
                />
                {selectedFile && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Selected: <span className="font-medium text-foreground">{selectedFile.name}</span>
                  </p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  Download the template first if you haven't already.
                </p>
              </div>

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelBulkImport}
                  disabled={isImporting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="default"
                  onClick={handleImportSubmit}
                  disabled={isImporting || !selectedFile}
                  loading={isImporting}
                  iconName="Upload"
                  iconPosition="left"
                >
                  {isImporting ? 'Importing...' : 'Import'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Survey Assignment Modal */}
      <SurveyAssignmentModal
        isOpen={isSurveyAssignmentModalOpen}
        onClose={handleSurveyAssignmentClose}
        onSuccess={handleSurveyAssignmentSuccess}
        property={selectedPropertyForSurvey}
        client={clients.find(c => 
          c.id === selectedPropertyForSurvey?.client ||
          c.id === parseInt(selectedPropertyForSurvey?.client_id) ||
          c.name === selectedPropertyForSurvey?.client ||
          c.name === selectedPropertyForSurvey?.bank_name
        ) || null}
        clients={clients}
        propertySurveyStatus={propertySurveyStatus}
      />
    </div>
  );
};

export default PropertyManagement;